import { FC } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { CareCreditPageImage } from '@frontend/payments-assets';
import { StripeSignupModal } from '@frontend/payments-setup';
import { PromoBannerBadge } from '../../../common/components/promo-banners/promo-banner-badge';
import { CareCreditLaunchBannerTextMap } from '../../constants';
import { useCareCreditLaunchBanner } from '../../hooks/useCareCreditLaunchBanner';
import { CareCreditLaunchBannerTrackingIds } from '../../tracking-ids';
import { PaymentsPromoBanner } from '../common/payments-promo-banner';

interface CareCreditPLGPromotionBannerProps {
  onClose?: () => void;
  innerRef?: React.LegacyRef<HTMLDivElement>;
  containerStyles?: Interpolation<Theme>;
}

const styles = {
  banner: css`
    .payments-promo-banner__right-col {
      padding-bottom: var(--fabric-space-padding-md);
    }
  `,
  featuredImageStyles: css`
    justify-self: flex-end;
    margin-top: -48px;
    margin-right: var(--fabric-space-padding-md);
    border-radius: var(--fabric-radius-md);
    box-shadow: 0 4px 8px 3px #00215226;
  `,
};

const CARE_CREDIT_LEARN_MORE_LINK = 'https://www.weavehelp.com/weavehelp/article/connecting-and-using-carecredit';

export const CareCreditPLGPromotionBanner: FC<CareCreditPLGPromotionBannerProps> = ({
  innerRef,
  onClose,
  containerStyles,
}) => {
  const { t } = useTranslation('highValueAdoption', { keyPrefix: 'payments' });
  const { showBanner, bannerType, markDismissed, primaryAction, stripeSignupModalProps } = useCareCreditLaunchBanner();

  if (!showBanner || !bannerType || !primaryAction) {
    return null;
  }

  return (
    <div ref={innerRef} css={[styles.banner, containerStyles]}>
      <PaymentsPromoBanner
        headerText={CareCreditLaunchBannerTextMap[bannerType].header}
        HeaderPrefix={() => <PromoBannerBadge badgeType='new' />}
        titleText={CareCreditLaunchBannerTextMap[bannerType].title}
        descriptionText={CareCreditLaunchBannerTextMap[bannerType].description}
        FeatureImage={CareCreditPageImage}
        featureImageStyles={styles.featuredImageStyles}
        footerActions={{
          primary: primaryAction,
          secondary: {
            text: t('Learn More'),
            trackingId: CareCreditLaunchBannerTrackingIds.learnMore,
            onClick: () => window.open(CARE_CREDIT_LEARN_MORE_LINK, '_blank', 'noopener,noreferrer'),
          },
          tertiary: {
            text: t('Maybe Later'),
            trackingId: CareCreditLaunchBannerTrackingIds.maybeLater,
            onClick: () => {
              markDismissed();
              if (onClose) {
                onClose();
              }
            },
          },
        }}
      />
      <StripeSignupModal {...stripeSignupModalProps} />
    </div>
  );
};
